// Central registry of admin-controllable typography roles used ACROSS the whole
// platform. Each role can be styled from Admin → Typography for Font + Size +
// Colour and is applied to the semantic class/selector it maps to (so a role
// controls that kind of text everywhere it appears — every page, section, card,
// explainer, note, etc.).
//
// - `key`        : storage key in settings.typography. Font -> `key`, size ->
//                  `key_size` (rem), colour -> `key_color` (hex).
// - `selector`   : CSS selector the size/colour (and font, when no `fontVar`)
//                  are applied to.
// - `fontVar`    : optional list of CSS variables the Font choice writes to
//                  (used where index.css reads the family via a variable so
//                  variable-based usages keep working). When absent, the font
//                  family is injected directly on `selector`.
// - `deva`       : true => offer Devanagari-capable fonts / preview in Hindi.
export const TG_GROUPS = [
  {
    group: "Titles & headings",
    note: "Page titles, section headings and card titles across the site.",
    roles: [
      { key: "heading", label: "Headings & titles (English)", help: "English page H1/H2 titles, section headings and card titles.", selector: ".font-serif-latin", fontVar: ["--font-heading", "--font-serif-latin"], deva: false },
      { key: "heading_deva", label: "Headings & titles (Devanagari)", help: "देवनागरी titles & headings (Hindi/Sanskrit).", selector: ".font-deva", fontVar: ["--font-heading-deva"], deva: true },
    ],
  },
  {
    group: "Body & reading text",
    note: "Long-form reading prose and general interface text.",
    roles: [
      { key: "reading", label: "Article body / reading prose", help: "Long-form reading text, meanings, descriptions and editorial body on every reading page.", selector: ".reading-content, .prose-content", fontVar: ["--font-reading"], deva: false },
      { key: "body", label: "Interface & UI text (English)", help: "Cards, form labels and general interface text (English).", selector: ".font-body", fontVar: ["--font-body"], deva: false },
      { key: "body_deva", label: "Interface & UI text (Hindi)", help: "Cards, labels and interface text (Hindi/Devanagari) — size Hindi independently of English.", selector: ".font-deva-body", fontVar: ["--font-body-deva"], deva: true },
    ],
  },
  {
    group: "Explainers, notes & references",
    note: "The smaller descriptive and editorial texts.",
    roles: [
      { key: "explainer", label: "Explainers & subheadings", help: "The descriptive text under section headings and page hero subtitles.", selector: ".tg-explainer", deva: true },
      { key: "note", label: "References & editorial notes", help: "Transliteration, sources, muhurta notes and editorial asides on reading pages.", selector: ".tg-note", deva: true },
    ],
  },
  {
    group: "Navigation & chrome",
    note: "Menu, buttons, brand wordmark, eyebrow labels and footer (top & bottom texts).",
    roles: [
      { key: "nav", label: "Navigation / menu links", help: "Top-bar menu links, dropdown items and the mobile menu list (both scripts).", selector: ".font-nav", fontVar: ["--font-nav"], deva: true },
      { key: "nav_category", label: "Menu categories & group headings", help: "Menu category names and mobile menu group headings.", selector: ".font-nav-category", fontVar: ["--font-nav-category"], deva: true },
      { key: "eyebrow", label: "Eyebrow / overline labels", help: "The small ALL-CAPS labels shown above headings across the site.", selector: ".overline-label", deva: true },
      { key: "brand", label: "Brand wordmark", help: "The धर्मार्थ / Dharmartha logo text in the header and footer.", selector: ".font-brand", fontVar: ["--font-brand"], deva: true },
      { key: "button", label: "Buttons & calls-to-action", help: "Primary button and CTA text.", selector: ".font-button", fontVar: ["--font-button"], deva: true },
      { key: "footer", label: "Footer text (bottom of every page)", help: "Footer description, column links and the copyright line.", selector: ".tg-footer", deva: true },
    ],
  },
];

export const TG_ROLES = TG_GROUPS.flatMap((g) => g.roles.map((r) => ({ ...r, group: g.group })));
export const TG_ROLE_KEYS = TG_ROLES.map((r) => r.key);

// Typography can be tuned GLOBALLY or scoped to a specific area/page so a change
// on one page never "plays up" on the rest of the site. Each non-global scope
// only overrides the roles you touch; everything else inherits the Global set.
export const TG_SCOPES = [
  { key: "global", label: "Global — all pages", help: "The base look used everywhere unless a page below overrides it." },
  { key: "home", label: "Homepage", help: "Only the homepage." },
  { key: "hub", label: "Knowledge Hub & articles", help: "Kathas, Mantras, Stotras, Suktas, Puja Vidhi, hub sections and all reading pages." },
  { key: "panchang", label: "Panchang & calendar", help: "Panchang, Choghadiya and the festival calendar pages." },
  { key: "deities", label: "Deities & Temples", help: "The Deities and Temples listing & detail pages." },
  { key: "services", label: "Services", help: "Pooja booking, astrology and numerology pages." },
  { key: "festivals", label: "Festivals", help: "Festival & observance landing pages." },
];
export const TG_SCOPE_KEYS = TG_SCOPES.map((s) => s.key);

// Map a route pathname to a typography scope (used to set body[data-tg-scope]).
export const scopeForPath = (pathname = "/") => {
  const p = (pathname || "/").toLowerCase();
  if (p === "/" || p === "") return "home";
  if (p.startsWith("/panchang") || p.startsWith("/choghadiya") || p.startsWith("/calendar") || p.startsWith("/hora")) return "panchang";
  if (p.startsWith("/deities") || p.startsWith("/temples") || p.startsWith("/deity") || p.startsWith("/temple")) return "deities";
  if (p.startsWith("/services") || p.startsWith("/pooja") || p.startsWith("/astrology") || p.startsWith("/numerology")) return "services";
  if (p.startsWith("/festivals") || p.startsWith("/festival") || p.startsWith("/observance")) return "festivals";
  return "hub";
};

// ── Per-language typography (platform-wide) ─────────────────────────────────
// Besides English + Hindi (the en/deva roles above), the site is read in many
// Indian languages. When a visitor picks one, <html lang="xx"> is set, so we can
// give EACH language its own Font + Size + Colour for every kind of text, applied
// across the whole platform. These roles are the de-duplicated (script-agnostic)
// version of the roles above — one control styles that kind of text for that
// language wherever it appears (it targets both the Latin and Devanagari class).
export const TG_LANG_ROLES = [
  { key: "heading", label: "Headings & titles", help: "Page titles, section headings and card titles.", selector: ".font-serif-latin, .font-deva" },
  { key: "reading", label: "Article body / reading prose", help: "Long-form reading text, meanings and descriptions.", selector: ".reading-content, .prose-content" },
  { key: "body", label: "Interface & UI text", help: "Cards, form labels and general interface text.", selector: ".font-body, .font-deva-body" },
  { key: "explainer", label: "Explainers & subheadings", help: "Descriptive text under headings and hero subtitles.", selector: ".tg-explainer" },
  { key: "note", label: "References & editorial notes", help: "Transliteration, sources and editorial asides.", selector: ".tg-note" },
  { key: "nav", label: "Navigation / menu links", help: "Top-bar menu links and the mobile menu list.", selector: ".font-nav" },
  { key: "nav_category", label: "Menu categories & group headings", help: "Menu category names and menu group headings.", selector: ".font-nav-category" },
  { key: "eyebrow", label: "Eyebrow / overline labels", help: "The small ALL-CAPS labels above headings.", selector: ".overline-label" },
  { key: "brand", label: "Brand wordmark", help: "The Dharmartha logo text in header and footer.", selector: ".font-brand" },
  { key: "button", label: "Buttons & calls-to-action", help: "Primary button and CTA text.", selector: ".font-button" },
  { key: "footer", label: "Footer text", help: "Footer description, column links and copyright line.", selector: ".tg-footer" },
];
export const TG_LANG_ROLE_KEYS = TG_LANG_ROLES.map((r) => r.key);

// Curated, script-appropriate Google Font families per Indic/other script.
export const SCRIPT_FONTS = {
  Latin: ["Cormorant Garamond", "EB Garamond", "Playfair Display", "Lora", "Merriweather", "Spectral", "Crimson Pro", "Libre Baskerville", "Work Sans", "Inter", "Poppins", "Source Sans 3", "DM Sans", "Manrope"],
  Devanagari: ["Tiro Devanagari Hindi", "Noto Serif Devanagari", "Noto Sans Devanagari", "Mukta", "Hind", "Baloo 2", "Martel", "Rozha One", "Kalam", "Yatra One"],
  Tamil: ["Noto Serif Tamil", "Noto Sans Tamil", "Mukta Malar", "Hind Madurai", "Catamaran", "Pavanam", "Arima", "Kavivanar"],
  Telugu: ["Noto Serif Telugu", "Noto Sans Telugu", "Mallanna", "Ramabhadra", "Suranna", "Mandali", "NTR", "Gidugu", "Tenali Ramakrishna", "Dhurjati"],
  Bengali: ["Noto Serif Bengali", "Noto Sans Bengali", "Hind Siliguri", "Baloo Da 2", "Galada", "Atma", "Mina", "Tiro Bangla"],
  Kannada: ["Noto Serif Kannada", "Noto Sans Kannada", "Baloo Tamma 2", "Benne"],
  Malayalam: ["Noto Serif Malayalam", "Noto Sans Malayalam", "Baloo Chettan 2", "Manjari", "Gayathri", "Chilanka"],
  Gujarati: ["Noto Serif Gujarati", "Noto Sans Gujarati", "Hind Vadodara", "Mukta Vaani", "Baloo Bhai 2", "Shrikhand", "Rasa", "Farsan"],
  Gurmukhi: ["Noto Serif Gurmukhi", "Noto Sans Gurmukhi", "Mukta Mahee", "Baloo Paaji 2"],
  Oriya: ["Noto Serif Oriya", "Noto Sans Oriya", "Baloo Bhaina 2"],
  Arabic: ["Noto Nastaliq Urdu", "Noto Naskh Arabic", "Noto Sans Arabic", "Amiri", "Lateef", "Scheherazade New"],
};

// CSS generic fallback appended after the chosen family, per script.
export const SCRIPT_FALLBACK = {
  Latin: "serif",
  Devanagari: "'Noto Serif Devanagari', serif",
  Tamil: "'Noto Serif Tamil', serif",
  Telugu: "'Noto Serif Telugu', serif",
  Bengali: "'Noto Serif Bengali', serif",
  Kannada: "'Noto Serif Kannada', serif",
  Malayalam: "'Noto Serif Malayalam', serif",
  Gujarati: "'Noto Serif Gujarati', serif",
  Gurmukhi: "'Noto Serif Gurmukhi', serif",
  Oriya: "'Noto Serif Oriya', serif",
  Arabic: "'Noto Naskh Arabic', serif",
};

// Language code → script. Covers the Indian languages the platform ships with.
export const LANG_SCRIPT = {
  en: "Latin",
  hi: "Devanagari", mr: "Devanagari", ne: "Devanagari", sa: "Devanagari", kok: "Devanagari", mai: "Devanagari", doi: "Devanagari", brx: "Devanagari",
  ta: "Tamil", te: "Telugu", bn: "Bengali", as: "Bengali", kn: "Kannada", ml: "Malayalam",
  gu: "Gujarati", pa: "Gurmukhi", or: "Oriya", ur: "Arabic", ks: "Arabic", sd: "Arabic",
};

export const scriptForLang = (code) => LANG_SCRIPT[(code || "").toLowerCase()] || "Latin";
export const fontsForLang = (code) => SCRIPT_FONTS[scriptForLang(code)] || SCRIPT_FONTS.Latin;
export const scriptFallbackFor = (code) => SCRIPT_FALLBACK[scriptForLang(code)] || "serif";

// Sensible built-in font defaults (only for the roles that had one historically).
export const TG_FONT_DEFAULTS = {
  heading: "Google Sans",
  heading_deva: "Noto Serif Devanagari",
  reading: "Google Sans",
  body: "Google Sans",
  body_deva: "Noto Sans Devanagari",
  nav: "Google Sans",
  nav_category: "Google Sans",
  brand: "Google Sans",
  button: "Google Sans",
};
